import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Dropdown, Menu, Button, message, Checkbox, Spin } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import {
  ActionIcon,
  ApprovalRoutingIcon,
  ApprovalUsersIcon,
  DeleteIcon,
  DiamondUpgradeIcon,
  DownloadInvoiceIcon,
  DropdownLinkPlusIcon,
  DropdownRenameIcon,
  EyePrivateIcon,
  EyePublicIcon,
  PlusIcon
} from '@icons';
import LottieComponent from '@components/Lottie';
import PageSortPanel from '@components/PageSortPanel';
import { useCurrentWorkspace } from '@hooks/workspace';
import useAssetSelection from '@hooks/useAssetSelection';
import useMarqueeSelection from '@hooks/useMarqueeSelection';
import { assetGetAssetsDownloadList, moveAsset } from '@api/Asset';
import './FolderItem.less';
import FolderEmptyState from '@pages/Campaigns/FolderEmptyState/FolderEmptyState';
import { AssetsList, FoldersList } from '@components/Assets';
import BulkActions from '@components/Assets/BulkActions';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import {
  toggleApprovalConflictAssetsModal,
  toggleCampaignFolderCreationModal,
  toggleChangeFolderAccessModal,
  toggleEntityDeletionModal,
  toggleManageFolderAccessModal,
  toggleMoveAssetToPrivateFolder,
  toggleRenameEntityModal,
  toggleShareAssetModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { moveAssetToFolder } from '@redux/actions/assetsAction';
import {
  AssetControllerListAssetsParams,
  AssetItemDto,
  CampaignItemDto
} from '@api/Api';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { useLayout } from '@layouts/PageLayout';
import { useOrganization } from '@components/OrganizationBoundary';
import { downloadAll } from '@helpers/download';
import { applyMarqueeAssetSelection } from '@helpers/applyMarqueeAssetSelection';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  useAssetsQuery,
  useAssetsCountQuery,
  useAssetQuery
} from '@hooks/queries/assets';
import { useDebounced } from '@helpers/useDebounced';
import { optimisticListItemRemove } from '@hooks/queries/helpers';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import { showTranscriptionReadyToast } from '@helpers/showTranscriptionReadyToast';

type AssetsOrderBy = NonNullable<
  AssetControllerListAssetsParams['orderBy']
>[number];

function FolderItemInWorkspace({
  folder,
  campaign
}: {
  folder: AssetItemDto;
  campaign: CampaignItemDto;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentView } = useLayout();
  const { currentOrganization } = useOrganization();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);
  const [orderBy, setOrderBy] = useState<AssetsOrderBy>('order:DESC');

  const [searchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const queryClient = useQueryClient();
  const prevFolderId = useRef(folder?.id);

  const assetsQuery = useAssetsQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      orderBy: [orderBy]
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id
    }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: false,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id,
      placeholderData: folder?.folder?.assetsCount
    }
  );

  const subfoldersQuery = useAssetsQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: true,
      searchQuery: debouncedSearchQuery,
      orderBy: [orderBy]
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id
    }
  );
  const subfoldersCountQuery = useAssetsCountQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: true,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id
    }
  );

  const allowUpload = !!campaign.permissions.createAssets && !campaign.demo;
  const isTileView = currentView === 'tile';

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const foldersList = useMemo(() => {
    return subfoldersQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [subfoldersQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);

  const foldersCount = subfoldersCountQuery.data ?? 0;
  const foldersToFetch = Math.max(0, foldersCount - foldersList.length);

  const {
    selected: selectedFolders,
    setSelected: setSelectedFolders,
    loading: loadingFolders,
    onSelectAll: onSelectAllFolders,
    setSelectedItems: setSelectedFoldersItems
  } = useAssetSelection(subfoldersQuery);
  const {
    selected: selectedAssets,
    setSelected: setSelectedAssets,
    loading: loadingAssets,
    onSelectAll: onSelectAllAssets,
    setSelectedItems: setSelectedAssetsItems
  } = useAssetSelection(assetsQuery);

  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const foldersListRef = useRef(foldersList);
  foldersListRef.current = foldersList;
  const assetsListRef = useRef(assetsList);
  assetsListRef.current = assetsList;

  const { marqueeBox } = useMarqueeSelection({
    containerRef: marqueeContainerRef,
    onSelect: (items) =>
      applyMarqueeAssetSelection({
        items,
        foldersList: foldersListRef.current,
        assetsList: assetsListRef.current,
        setSelectedFolders,
        setSelectedAssets
      })
  });

  useWebSocketSubscription((msg) => {
    if (!folder) return;
    if (msg.causedByMe) return;
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  }, 'folder-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!folder || msg.payload.folderId !== folder.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!folder || msg.payload.folderId !== folder.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-access-changed'
  );

  useWebSocketSubscription((msg) => {
    if (!campaign || msg.payload.campaignId !== campaign.id) return;
    if (msg.causedByMe) return;
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, 'campaign-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-access-changed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    (messages) => {
      const msg = messages[messages.length - 1];
      const payload = msg?.payload;
      if (payload?.reason === 'transcription-completed') {
        showTranscriptionReadyToast({
          payload,
          onViewTranscript: () => {
            navigate(
              `/media-viewer/${payload.assetId}/${payload.assetVersionId}`
            );
          }
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  const upgradePrivateFolders =
    !currentOrganization.entity?.features.privateFolders;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization?.entity?.features.approvalRouting;

  const menuItems: ItemType[] = [
    {
      key: 'download-files',
      disabled: folder?.folder?.totalAssetsCount === 0,
      label: 'Download files',
      icon: <DownloadInvoiceIcon />,
      onClick: async () => {
        if (!folder?.folder) return;
        const {
          data: { list }
        } = await assetGetAssetsDownloadList({
          campaignId: folder.campaignId,
          workspaceId: folder.workspaceId,
          parentId: folder.id
        });
        downloadAll(list, `${folder.folder.name}.zip`, folder.id);
      }
    },
    {
      key: 'new-share-link',
      label: 'New share link',
      disabled:
        !campaign?.permissions.createReviews ||
        !folder?.folder?.totalAssetsCount,
      icon: <DropdownLinkPlusIcon />,
      onClick: () => {
        if (!campaign?.permissions.createReviews) return;
        dispatch(
          toggleShareAssetModal({
            visible: true,
            campaignId: folder.campaignId,
            folders: [folder.id],
            defaultName: folder.folder?.name || ''
          })
        );
      }
    },
    {
      type: 'divider'
    },
    ...(folder?.folder?.permissions.rename
      ? [
          {
            key: 'rename-folder',
            label: 'Rename',
            icon: <DropdownRenameIcon />,
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  entity: folder as AssetItemDto,
                  page: 'folder',
                  visible: true
                })
              );
            }
          },
          {
            type: 'divider' as const
          }
        ]
      : []),
    ...(folder?.folder?.permissions.setVisibility
      ? [
          {
            key: 'toggle-access',
            label: (
              <UpgradePlanTooltip
                text=" to a Team plan to access this feature."
                isCanVisible={!folder.isPrivate && upgradePrivateFolders}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                  }}
                >
                  {folder?.isPrivate ? 'Make public' : 'Make private'}

                  {!folder.isPrivate && upgradePrivateFolders && (
                    <DiamondUpgradeIcon className="upgrade-icon" />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            disabled: !folder.isPrivate && upgradePrivateFolders,
            icon: folder?.isPrivate ? <EyePublicIcon /> : <EyePrivateIcon />,
            onClick: () => {
              if (!folder.isPrivate && upgradePrivateFolders) return;
              if (!folder?.folder) return;
              dispatch(
                toggleChangeFolderAccessModal({
                  folder,
                  visible: true,
                  callback: async (folder: AssetItemDto) => {
                    if (!folder.isPrivate) return;
                    dispatch(
                      toggleManageFolderAccessModal({
                        folder,
                        visible: true
                      })
                    );
                  }
                })
              );
            }
          }
        ]
      : []),
    ...(folder?.folder?.permissions?.inviteContributors ||
    folder?.folder?.permissions?.inviteFromList
      ? [
          {
            key: 'manage-users',
            label: 'Manage users',
            icon: <ApprovalUsersIcon />,
            onClick: () => {
              if (!folder) return;
              dispatch(
                toggleManageFolderAccessModal({ folder, visible: true })
              );
            }
          }
        ]
      : []),
    ...(folder?.folder?.permissions?.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            disabled: !folder?.folder?.permissions.delete,
            icon: <DeleteIcon />,
            onClick: () => {
              if (!folder?.folder?.permissions.delete) return;
              dispatch(
                toggleEntityDeletionModal({
                  entity: folder as AssetItemDto,
                  page: 'folder',
                  permanent: false,
                  visible: true,
                  func: () => navigate(`../`)
                })
              );
            }
          }
        ]
      : [])
  ];

  return (
    <div>
      <div className="header-container">
        <div className="title-container">
          <span className="folder-name">
            {folder?.isPrivate && (
              <div>
                <EyePrivateIcon />
              </div>
            )}
            <div>{folder?.folder?.name}</div>
          </span>
          <Dropdown
            menu={{
              items: menuItems,
              onClick: (e) => {
                e.domEvent.stopPropagation();
              }
            }}
            overlayClassName="asset_links_overlay"
            placement="bottomLeft"
            trigger={['click']}
          >
            <Button
              className="header-container__dropdown-trigger"
              type="text"
              size="small"
            >
              <ActionIcon />
            </Button>
          </Dropdown>
        </div>
        <UpgradePlanTooltip
          text={
            !canUseBatchApprovals || !awFeatureEnabled
              ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
              : 'Click to route all media in folder for approval'
          }
          isCanVisible={true}
          placement="topRight"
          isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
          innerStyle={{
            maxWidth: '300px'
          }}
        >
          <>
            <Button
              type="default"
              size="small"
              className="info-button"
              disabled={!canUseBatchApprovals || !awFeatureEnabled}
              onClick={() => {
                if (!canUseBatchApprovals || !awFeatureEnabled) return;
                dispatch(
                  toggleApprovalConflictAssetsModal({
                    visible: true,
                    title: 'Route folder for approval',
                    campaignId: folder?.campaignId,
                    workspaceId: folder?.workspaceId,
                    parentId: folder?.id,
                    callback: (aw) => {
                      if (aw) {
                        navigate(
                          `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                        );
                        return;
                      }
                      queryClient.invalidateQueries({ queryKey: ['assets'] });
                    }
                  })
                );
              }}
            >
              <span
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}
              >
                <ApprovalRoutingIcon className="info-button-icon" />
                <span>Route folder for approval</span>
                {(!canUseBatchApprovals || !awFeatureEnabled) && (
                  <DiamondUpgradeIcon className="upgrade-icon" />
                )}
              </span>
            </Button>
          </>
        </UpgradePlanTooltip>
      </div>
      <PageSortPanel
        entity="in folder"
        onAddClick={
          campaign?.demo
            ? undefined
            : () => {
                if (!campaign) return;
                dispatch(
                  toggleUploadAssetModal({
                    visible: true,
                    workspaceId: campaign.workspaceId,
                    campaignId: campaign.id,
                    folderId: folder.id
                  })
                );
              }
        }
        actionButtonName="Upload new media"
        onSearch={setSearchQuery}
        onOrderBy={undefined}
        classNameSearchContainer={undefined}
        classNameActionButton={undefined}
        sortMenuModal={
          <Menu
            selectedKeys={[orderBy]}
            onClick={async ({ key }) => setOrderBy(key as AssetsOrderBy)}
          >
            <span className="help_dropdown__title">Sort by</span>
            <Menu.Item key="order:DESC">
              <span className="help_dropdown__dot" /> Custom
            </Menu.Item>
            <Menu.Item key="createdAt:DESC">
              <span className="help_dropdown__dot" /> Date created
            </Menu.Item>
            <Menu.Item key="modifiedAt:DESC">
              <span className="help_dropdown__dot" /> Date modified
            </Menu.Item>
            <Menu.Item key="name:ASC">
              <span className="help_dropdown__dot" /> Name
            </Menu.Item>
          </Menu>
        }
        searchInputComponent={undefined}
        classNamePanel={undefined}
      />
      <div
        ref={marqueeContainerRef}
        className="menu-panel-container marquee-selection-container"
        style={{
          overflow: 'auto',
          marginTop: 24,
          paddingRight: 34,
          paddingLeft: 34,
          marginRight: -34,
          marginLeft: -34,
          height: 'calc(100vh - 275px - var(--demo-mode-panel-width, 0px))'
        }}
      >
        {marqueeBox}
        <div className="campaign_items">
          <div className="campaign_items__top">
            <button
              type="button"
              className="campaign_items__title"
              disabled={campaign?.demo || !campaign?.permissions.createAssets}
              onClick={() => {
                if (campaign?.demo || !campaign?.permissions.createAssets)
                  return;
                dispatch(
                  toggleCampaignFolderCreationModal({
                    entity: 'folder',
                    campaignId: folder.campaignId,
                    parentId: folder.id,
                    visible: true
                  })
                );
              }}
            >
              {foldersCount ? 'Folders' : 'Add folder'}{' '}
              {!campaign?.demo && campaign?.permissions.createAssets && (
                <PlusIcon />
              )}
            </button>
            {!!foldersList?.length && isTileView && (
              <Checkbox
                disabled={loadingFolders}
                checked={
                  !!selectedFolders.length &&
                  selectedFolders.length === foldersCount
                }
                indeterminate={
                  !!selectedFolders.length &&
                  selectedFolders.length < foldersCount
                }
                onChange={onSelectAllFolders}
              >
                Select all
              </Checkbox>
            )}
          </div>

          <Spin spinning={loadingFolders}>
            <InfiniteScroll
              loading={
                !loadingFolders && subfoldersQuery.isFetchingNextPage
                  ? 'more'
                  : false
              }
              next={subfoldersQuery.fetchNextPage}
              hasMore={subfoldersQuery.hasNextPage ?? false}
            >
              <FoldersList
                canShare={campaign?.permissions.createReviews}
                selectedFolders={selectedFolders}
                foldersCount={foldersCount}
                setSelectedFolders={setSelectedFoldersItems}
                foldersList={foldersList}
                countToFetch={
                  !loadingFolders &&
                  (subfoldersQuery.isLoading ||
                    subfoldersQuery.isFetchingNextPage)
                    ? foldersToFetch
                    : 0
                }
                isCardViewMode={isTileView}
                canReorganize={orderBy === 'order:DESC'}
                canDropInside={true}
                listLoading={subfoldersQuery.isLoading}
                onAssetDrop={(assetId, folderId) => {
                  const asset = assetsList
                    .map((x) => x.node)
                    .find((x) => x.id === assetId);
                  const targetFolder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === folderId);
                  if (!asset?.asset || !targetFolder?.folder) return;
                  const { permissions } = asset.asset;
                  const canMove =
                    (targetFolder.isPrivate &&
                      permissions.moveToPrivateFolder) ||
                    (!targetFolder.isPrivate && permissions.moveToPublicFolder);
                  if (!canMove) return;
                  const version = asset.asset.versions[0];
                  const onConfirm = () =>
                    dispatch(moveAssetToFolder({ assetId, folderId }));
                  if (targetFolder.isPrivate) {
                    dispatch(
                      toggleMoveAssetToPrivateFolder({
                        visible: true,
                        folderName: targetFolder.folder.name,
                        assetsName: [`${version.name}.${version.extension}`],
                        callback: onConfirm
                      })
                    );
                  } else onConfirm();
                }}
                onFolderDrop={async (sourceFolderId, targetFolderId) => {
                  if (sourceFolderId === targetFolderId) return;
                  const sourceFolder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === sourceFolderId);
                  const targetFolder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === targetFolderId);
                  if (!sourceFolder?.folder || !targetFolder?.folder) return;
                  const { permissions } = sourceFolder.folder;
                  const canMove = targetFolder.isPrivate
                    ? permissions.moveToPrivateFolder
                    : permissions.moveToPublicFolder;
                  if (!canMove) return;
                  const onConfirm = async () => {
                    await queryClient.cancelQueries({ queryKey: ['assets'] });
                    optimisticListItemRemove<AssetItemDto>({
                      queryKey: ['assets', 'list'],
                      id: sourceFolderId
                    });
                    await moveAsset({
                      assetId: sourceFolderId,
                      folderId: targetFolderId
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ['assets']
                    });
                  };
                  if (!sourceFolder.isPrivate && targetFolder.isPrivate) {
                    dispatch(
                      toggleMoveAssetToPrivateFolder({
                        visible: true,
                        folderName: targetFolder.folder.name,
                        assetsName: [sourceFolder.folder.name],
                        callback: onConfirm
                      })
                    );
                  } else {
                    await onConfirm();
                  }
                }}
              />
            </InfiniteScroll>
          </Spin>
        </div>

        {assetsCount > 0 ? (
          <Spin spinning={loadingAssets}>
            <InfiniteScroll
              loading={
                !loadingAssets && assetsQuery.isFetchingNextPage
                  ? 'more'
                  : false
              }
              next={assetsQuery.fetchNextPage}
              hasMore={assetsQuery.hasNextPage ?? false}
            >
              <div className="campaign_items__top">
                <button
                  type="button"
                  className="campaign_items__title"
                  onClick={() => {
                    if (!allowUpload) return;
                    dispatch(
                      toggleUploadAssetModal({
                        visible: true,
                        workspaceId: folder.workspaceId,
                        campaignId: folder.campaignId,
                        folderId: folder.id
                      })
                    );
                  }}
                >
                  Media {allowUpload && <PlusIcon />}
                </button>
                {!!assetsList?.length && isTileView && (
                  <Checkbox
                    disabled={loadingAssets}
                    checked={
                      !!selectedAssets.length &&
                      selectedAssets.length === assetsCount
                    }
                    indeterminate={
                      !!selectedAssets.length &&
                      selectedAssets.length < assetsCount
                    }
                    onChange={onSelectAllAssets}
                  >
                    Select all
                  </Checkbox>
                )}
              </div>
              <AssetsList
                campaign={campaign}
                folderId={folder.id}
                searchQuery={searchQuery}
                selectedAssets={selectedAssets}
                setSelectedAssets={setSelectedAssetsItems}
                assetsList={assetsList}
                displayAssetsCount={assetsCount}
                isTileView={isTileView}
                page="campaign"
                canCombine={true}
                countToFetch={
                  !loadingAssets && assetsQuery.isFetchingNextPage
                    ? assetsToFetch
                    : 0
                }
                listLoading={assetsQuery.isLoading}
                canReorganize={orderBy === 'order:DESC'}
                canShare={!!campaign?.permissions.createReviews}
                isShowCompany={false}
              />
            </InfiniteScroll>
          </Spin>
        ) : (
          <FolderEmptyState campaignId={campaign.id} folderId={folder.id} />
        )}
        <BulkActions
          selectedAssets={selectedAssets}
          selectedFolders={selectedFolders}
          canShare={!!campaign?.permissions.createReviews}
          page="campaign"
          onRefreshAssetList={assetsQuery.refetch}
          onClose={() => {
            setSelectedAssets([]);
            setSelectedFolders([]);
          }}
        />
      </div>
    </div>
  );
}

function FolderItem() {
  const { campaignId, folderId } = useParams();
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);

  const campaignQuery = useCampaignQuery(
    { campaignId: campaignId || '' },
    { enabled: !!campaignId }
  );
  const campaign = campaignQuery?.data;
  const campaignError = campaignQuery?.error;
  const folderQuery = useAssetQuery(
    {
      assetId: folderId || '',
      isFolder: true
    },
    { enabled: !!folderId }
  );
  const folderError = folderQuery?.error;
  const folder = folderQuery?.data;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAxiosError(campaignError)) return;
    if (campaignQuery.isFetching) return;
    if (!currentWorkspace?.id) return;
    const status = campaignError?.response?.status;
    if (status !== 403 && status !== 404) return;
    message.error('This project is no longer available.');
    navigate(`/campaigns/all/${currentWorkspace.id}`);
  }, [campaignError, campaignQuery.isFetching, currentWorkspace?.id, navigate]);

  useEffect(() => {
    if (!isAxiosError(folderError)) return;
    if (folderQuery.isFetching) return;
    const status = folderError?.response?.status;
    if (status !== 403 && status !== 404) return;
    message.error('This folder is no longer available.');
    navigate('../');
  }, [folderError, folderQuery.isFetching, navigate]);

  useEffect(() => {
    if (!campaign) return;
    if (campaign.workspaceId === currentWorkspace?.id) return;
    setCurrentWorkspace(campaign.workspaceId);
  }, [campaign, currentWorkspace, setCurrentWorkspace]);

  const fullPageLoader = (
    <div className="page_spinner_overlay">
      <LottieComponent className="page_spinner" view="preloader" />
    </div>
  );

  if (!folder || !campaign || !currentWorkspace) {
    return fullPageLoader;
  }
  if (campaign.workspaceId !== currentWorkspace.id) {
    return fullPageLoader;
  }
  if (folder.campaignId !== campaign.id) return null;
  return <FolderItemInWorkspace folder={folder} campaign={campaign} />;
}

export default FolderItem;
